import { useTable } from '@refinedev/antd';
import { Table, Tag, Tooltip } from 'antd';
import { Building2, Key } from 'lucide-react';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';

interface OrganizationRecord {
  id: string;
  name: string;
  slug: string;
  plan: string;
  userCount: number;
  projectCount: number;
  feedbackCount: number;
  createdAt: string;
}

const PLAN_COLORS: Record<string, { color: string; text: string }> = {
  free: { color: 'default', text: 'Free' },
  starter: { color: 'blue', text: 'Starter' },
  pro: { color: 'blue', text: 'Pro' },
  team: { color: 'purple', text: 'Team' },
  business: { color: 'gold', text: 'Business' },
  enterprise: { color: 'green', text: 'Enterprise' },
};

const OrganizationListPage: FC = () => {
  const navigate = useNavigate();
  const { tableProps } = useTable<OrganizationRecord>({
    resource: 'admin/organizations',
    pagination: { pageSize: 20 },
  });

  return (
    <div className="space-y-6">
      {/* Header gradient */}
      <div className="fc-gradient-header rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3">
          <Building2 className="h-6 w-6" />
          <div>
            <h1 className="text-xl font-bold">Organisations</h1>
            <p className="mt-1 text-sm text-blue-100">Gestion des comptes entreprise</p>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="fc-card overflow-hidden p-0">
        <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-700">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
            Toutes les organisations
          </h3>
        </div>
        <Table {...tableProps} rowKey="id">
          <Table.Column dataIndex="name" title="Nom" />
          <Table.Column
            dataIndex="slug"
            title="Slug"
            render={(slug: string) => (
              <span className="font-mono text-xs text-slate-500 dark:text-slate-400">{slug}</span>
            )}
          />
          <Table.Column
            dataIndex="plan"
            title="Plan"
            render={(plan: string) => {
              const cfg = PLAN_COLORS[plan] ?? { color: 'default', text: plan ?? 'free' };
              return <Tag color={cfg.color}>{cfg.text}</Tag>;
            }}
          />
          <Table.Column dataIndex="userCount" title="Utilisateurs" />
          <Table.Column dataIndex="projectCount" title="Projets" />
          <Table.Column dataIndex="feedbackCount" title="Feedbacks" />
          <Table.Column
            dataIndex="createdAt"
            title="Créée le"
            render={(date: string) => (
              <span className="text-slate-500 dark:text-slate-400">
                {date ? new Date(date).toLocaleDateString('fr-FR') : '—'}
              </span>
            )}
          />
          <Table.Column
            title="Actions"
            render={(_: unknown, record: OrganizationRecord) => (
              <Tooltip title="Configuration BYOK">
                <button
                  onClick={() => navigate(`/ops/organizations/${record.id}/byok`)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                  <Key className="h-3.5 w-3.5" /> BYOK
                </button>
              </Tooltip>
            )}
          />
        </Table>
      </div>
    </div>
  );
};

export default OrganizationListPage;
